import { ConnectorPickerBlock } from "@/components/artifacts/view/blocks/ConnectorPickerBlock.tsx";
import { AccessChoiceList } from "@/components/connectors/AccessChoice.tsx";
import {
  ACCESS_CHOICE_TITLE,
  CONNECTOR_PROMPT_ACCESS_INTRO,
  connectorPromptAccessItems,
} from "@/shared/accessSelection.ts";
import { useAccessChoice } from "@/shared/useAccessChoice.ts";
import {
  connectorPromptPicksQuery,
  connectorPromptQuery,
  useRecordConnectorPromptShown,
} from "@/components/connectors/connectorPromptQueries.ts";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { isStepUpGatedPath } from "@/lib/stepUp.ts";
import { useStepUp } from "@/lib/useStepUp.ts";
import { resolveApiPath } from "@/lib/utils.ts";
import { providerName } from "@/views/authProviders.ts";
import {
  accessContinuePath,
  type GetConnectorPromptResp,
  PROMPT_PROVIDERS,
  promptRecommendedIds,
} from "@/types/connectorPrompt.ts";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

type DuePrompt = Extract<GetConnectorPromptResp, { due: true }>;

/**
 * The connector prompt (`docs/prds/onboarding.md`, The connect modal), on app
 * open. With none of mail, calendar or contacts connected, it is the sign-up
 * access screen — "Choose what Okthx can use" — asking for all three in one
 * consent. With some of them, it is the connector picker card, recommending
 * the missing one from the user's own provider and the services picked for
 * them.
 *
 * The server decides whether it is due; this asks once per load and reports
 * the showing back, which starts the wait before the next one and marks the
 * cached answer not due. The showing is held here rather than read from the
 * cache, so it stays open after that. Inside the iOS app's WebView the native
 * app shows its own prompt instead.
 */
export const ConnectorPromptModal = () => {
  const isWebView = typeof window !== "undefined" && window.isWebView === true;
  const { data } = useQuery({ ...connectorPromptQuery, enabled: !isWebView });
  const { mutate: recordShown } = useRecordConnectorPromptShown();
  const [shown, setShown] = useState<DuePrompt>();
  const recorded = useRef(false);
  const close = useCallback(() => setShown(undefined), []);

  useEffect(() => {
    if (!data?.due || recorded.current) return;
    recorded.current = true;
    setShown(data);
    recordShown();
  }, [data, recordShown]);

  if (!shown) return null;
  return shown.state === "none" ? (
    <AccessPromptDialog prompt={shown} onClose={close} />
  ) : (
    <PickerPromptDialog prompt={shown} onClose={close} />
  );
};

/** Nothing core connected: the sign-up access screen, in a dialog. */
const AccessPromptDialog = ({ prompt, onClose }: { prompt: DuePrompt; onClose: () => void }) => {
  const { offered, selected, toggle } = useAccessChoice(prompt.provider ?? "google");
  const stepUp = useStepUp();
  // A phone or email signup has no provider yet, so it is offered both.
  const providers = prompt.provider ? [prompt.provider] : PROMPT_PROVIDERS;

  // The connect runs in its own tab, so a cancel or error there leaves this
  // open to try again; only the tab's report of a connect closes it.
  useEffect(() => {
    const offered: readonly string[] = prompt.provider ? [prompt.provider] : PROMPT_PROVIDERS;
    const channel = new BroadcastChannel("oauth_result");
    channel.onmessage = (
      event: MessageEvent<{ provider?: string; success?: boolean; flow?: string }>,
    ) => {
      // A sign-in or added account broadcasts on the same channel, and
      // connects nothing this asked for.
      const { provider, success, flow } = event.data;
      if (flow === "connect" && success && provider !== undefined && offered.includes(provider)) {
        onClose();
      }
    };
    return () => channel.close();
  }, [prompt.provider, onClose]);

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-6 sm:max-w-lg">
        <DialogHeader className="items-center text-center">
          <DialogTitle className="text-2xl font-semibold">{ACCESS_CHOICE_TITLE}</DialogTitle>
          <DialogDescription className="leading-6">
            {CONNECTOR_PROMPT_ACCESS_INTRO}
          </DialogDescription>
        </DialogHeader>
        <div className="-mx-1 min-h-0 overflow-y-auto px-1">
          <AccessChoiceList
            offered={connectorPromptAccessItems(offered)}
            selected={selected}
            onToggle={toggle}
          />
        </div>
        <div className="flex flex-col items-center gap-2">
          {providers.map((provider) => {
            const path = resolveApiPath(
              accessContinuePath({
                provider,
                signInConnectorId: prompt.signInConnectorId,
                access: selected,
              }),
            );
            // A number proven only by a claim link must be proven first; that
            // page brings the user back here.
            const stepUpFirst = stepUp.required && isStepUpGatedPath(path);
            const label = `Continue with ${providerName(provider)}`;
            let content = <span>{label}</span>;
            if (selected.length > 0 && stepUpFirst) {
              content = <a href={stepUp.href}>{label}</a>;
            } else if (selected.length > 0) {
              content = (
                <a href={path} target="_blank" rel="noopener noreferrer">
                  {label}
                </a>
              );
            }
            return (
              <Button
                key={provider}
                asChild={selected.length > 0}
                disabled={selected.length === 0}
                className="h-12 px-8"
                data-statsig-name="connector-prompt-continue"
              >
                {content}
              </Button>
            );
          })}
          <Button variant="ghost" onClick={onClose} data-statsig-name="connector-prompt-not-now">
            Not now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** Some core connected: the connector picker card, recommending what fits. */
const PickerPromptDialog = ({ prompt, onClose }: { prompt: DuePrompt; onClose: () => void }) => {
  const picks = useQuery(connectorPromptPicksQuery(true));
  const pinnedIds = promptRecommendedIds(
    prompt.pinnedIds,
    (picks.data?.items ?? []).map((item) => item.id),
  );

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      {/* Bounded by the viewport and scrolled within, so the footer stays
          reachable on a short window. */}
      <DialogContent
        className="flex max-h-[90vh] flex-col sm:max-w-2xl"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>Connect more of your tools</DialogTitle>
        </DialogHeader>
        <div className="-mx-1 min-h-0 overflow-y-auto px-1">
          <ConnectorPickerBlock scopeIds={[]} pinnedIds={pinnedIds} preferredIds={[]} inPlace />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-statsig-name="connector-prompt-not-now">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
